import React, { ReactElement, useState } from 'react';
import { useMutation } from 'react-query';

import { logsApi } from 'api/bestpark/logs';
import { reservationsApi } from 'api/bestpark/reservations';
import { ReactComponent as Loader } from 'assets/svgs/loading.svg';
import { LOCAL_STORAGE_RESERVATION_DATA } from 'constants/local-storage';
import { useAuth } from 'contexts/auth-context';
import { useReservations } from 'contexts/reservations-context';
import { setItem } from 'helpers/local-storage';
import { buildAncillaryApiData } from 'helpers/mappers/reservation-mapper';
import Stripe from 'helpers/stripe';
import { eraseCookie } from 'helpers/cookie-helper';
import { useTranslate } from 'hooks/translate';

import './booking-payment-processing.scss';
import { CONFIRMATION_PATH, HOMEPAGE_PATH } from 'constants/paths';
import useNav from 'hooks/nav';
import { calculatePriceAfterDiscount } from 'helpers/coupon-helper';

interface KeyedGuestGroups {
  [key: number]: ParkGuestGroup;
}

/**
 * Handle redirection to Stripe Checkout page
 * @TODO - this should be defined at the payment option subcomponent once refactored
 * and the parent component (page) will just execute the callback enforced by an interface
 * that needs to be implemented as well
 * Sticking to this dirty approach for now
 *
 * @param {Reservation} data Reservation data
 */
const initiateStripeRedirect = async (data: Reservation): Promise<void> => {
  try {
    const stripe = await Stripe(data.stripeConnectAccountId as string);
    const result = await stripe.redirectToCheckout({
      sessionId: `${data.stripeCheckoutSessionId}`,
    });
    if (result.error) {
      // @TODO - handle failure in redirection
      // @TODO - redirect with query param `stripeStatus` with
      // the values 'canceled' if transaction is canceled,
      // 'success' if it's a success
    }
  } catch (error) {
    console.log(error);
    logsApi.postLog('Reservation', 'Payment Failed', error);
  }
};

let initialCall = true;

export default function BookingPaymentProcessing(): ReactElement {
  const [error, setError] = useState<string>();
  const { authState, user } = useAuth();
  const { goTo } = useNav();

  const {
    endDate,
    parkId,
    passengerDeclarations,
    services,
    slot,
    slotFee,
    startDate,
    guests,
    parkToReserve: data,
    costBreakdown,
    appliedCoupon,
  } = useReservations();
  const { locale, t } = useTranslate();
  const basePrice: number = costBreakdown?.base?.total ?? 0;
  const tax: number = Number(basePrice * 0.12);
  const membership: number = Number(basePrice * 0.5);
  let totalPrice: number = basePrice + tax - membership;

  if (appliedCoupon) {
    const { discountType, discountAmount } = appliedCoupon;
    totalPrice = calculatePriceAfterDiscount(
      totalPrice,
      discountType,
      discountAmount,
    );
  }

  let guestGroups: KeyedGuestGroups = {};
  if (data?.guestGroups) {
    guestGroups = data?.guestGroups?.reduce((accumulator, current) => {
      accumulator[current.id] = current;
      return accumulator;
    }, {} as KeyedGuestGroups); // eslint-disable-line @typescript-eslint/consistent-type-assertions
  }

  const onSuccess = async (data: Reservation): Promise<void> => {
    data.total = totalPrice;

    if (totalPrice > 0) {
      await initiateStripeRedirect(data);
    }

    setItem(LOCAL_STORAGE_RESERVATION_DATA, JSON.stringify(data));
    // Erase cookie
    eraseCookie('search');

    if (totalPrice === 0) {
      goTo(`${CONFIRMATION_PATH}/${data.id}`);
    }
  };

  const onError = (error?: any): void => {
    if (!error) setError(t('errors.default'));
  };
  const [postReservation] = useMutation(reservationsApi.postReservation, {
    onSuccess,
    onError,
  });

  if (!user || !parkId) {
    goTo(HOMEPAGE_PATH);
    return <></>;
  }
  if (initialCall)
    postReservation({
      data: {
        parkCost: costBreakdown?.base?.parkCost,
        startDate,
        endDate,
        parkId: parkId,
        ancillaries: buildAncillaryApiData(
          services,
          new Date(startDate),
          new Date(endDate),
        ),
        total: totalPrice,
        settledAmount: totalPrice,
        couponId: appliedCoupon?.id ?? null,
        customerId: user?.id ?? 0,
        passengerDeclarations,
        locale,
        slot,
        slotFee,

        // since post reservation is expecting the number of individuals as the number of total guests, including the inclusions
        // let's just add the additional guests + inclusions for now until the API gets updated
        guests: guests.map((guest) => {
          return {
            ...guest,
            numberOfIndividuals:
              (guestGroups[guest.guestGroupId]?.inclusions ?? 0) +
              (guest.numberOfIndividuals ?? 0),
          };
        }),
      },
      token: authState?.accessToken ?? '',
    }).catch(() => onError());

  initialCall = false;

  return (
    <div className='booking-payment-processing'>
      {error ? (
        <h2>{error}</h2>
      ) : (
        <div className='payment-loading'>
          <Loader />
          <p>{t('reservation.payment.processingPayment')}</p>
        </div>
      )}
    </div>
  );
}
